import { useQuery } from "@tanstack/react-query";
import { adminQueries } from "./admin-query";
import {
  EmptyState,
  MetricCard,
  PageHeader,
  PanelState,
  RefreshFailedNotice,
} from "./AdminUi";
import { dashboardWarnings, formatDate } from "./admin-model";
import { Icon } from "./Icon";

export function OverviewPanel() {
  const usersQuery = useQuery(adminQueries.users());
  const applicationsQuery = useQuery(adminQueries.applications());
  const providerQuery = useQuery(adminQueries.provider());
  const securityQuery = useQuery(adminQueries.security());

  const refreshFailed =
    usersQuery.isError ||
    applicationsQuery.isError ||
    providerQuery.isError ||
    securityQuery.isError;
  // Only a failure with nothing cached is fatal; a failed background refetch
  // keeps the panel on screen.
  if (
    !usersQuery.data ||
    !applicationsQuery.data ||
    !providerQuery.data ||
    !securityQuery.data
  ) {
    return <PanelState error={refreshFailed} label="overview" />;
  }

  const { users } = usersQuery.data;
  const { applications } = applicationsQuery.data;
  const { provider } = providerQuery.data;
  const auditEvents = securityQuery.data.events;
  const warnings = dashboardWarnings(applications, provider);
  return (
    <>
      <PageHeader
        action={
          <a
            className="button button--secondary"
            href={provider.endpoints.discovery}
            rel="noreferrer"
            target="_blank"
          >
            Open discovery
            <Icon name="external" size={15} />
          </a>
        }
        description="Operational status, risks, and recent configuration activity."
        eyebrow="Control center"
        title="Identity provider"
      />

      {refreshFailed && <RefreshFailedNotice />}

      <section
        className="stat-grid stat-grid--four"
        aria-label="Provider summary"
      >
        <MetricCard
          detail={`${applications.length} registered`}
          label="Active applications"
          tone="positive"
          value={applications.filter((item) => item.status === "active").length}
        />
        <MetricCard
          detail={`${users.length} directory accounts`}
          label="Active users"
          tone="positive"
          value={users.filter((item) => item.status === "active").length}
        />
        <MetricCard
          detail={`Current: ${provider.signingKeys.currentKid ?? "Unavailable"}`}
          label="Signing keys"
          value={provider.signingKeys.published}
        />
        <MetricCard
          detail="Actionable findings"
          label="Needs attention"
          tone={warnings.length ? "warning" : "positive"}
          value={warnings.length}
        />
      </section>

      <div className="dashboard-grid">
        <section className="panel-card panel-card--wide">
          <header>
            <div>
              <span className="eyebrow">Review</span>
              <h2>Needs attention</h2>
            </div>
            <a href="/admin/applications">View applications</a>
          </header>
          {warnings.length ? (
            <div className="attention-list">
              {warnings.slice(0, 8).map((warning, index) => (
                <article
                  key={`${warning.code}-${warning.applicationId ?? index}`}
                >
                  <span aria-hidden="true">
                    <Icon name="warning" size={13} />
                  </span>
                  <div>
                    <strong>{warning.title}</strong>
                    <p>{warning.detail}</p>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <EmptyState
              hint="Registered applications meet the current checks."
              title="No critical warnings"
            />
          )}
        </section>

        <section className="panel-card">
          <header>
            <div>
              <span className="eyebrow">Provider</span>
              <h2>Protocol status</h2>
            </div>
            <a href="/admin/provider">Details</a>
          </header>
          <dl className="facts-list">
            <div>
              <dt>Issuer</dt>
              <dd>
                <code>{provider.issuer}</code>
              </dd>
            </div>
            <div>
              <dt>Flow</dt>
              <dd>Authorization Code</dd>
            </div>
            <div>
              <dt>PKCE</dt>
              <dd>S256 required</dd>
            </div>
            <div>
              <dt>Signing</dt>
              <dd>{provider.invariants.signingAlgorithm}</dd>
            </div>
            <div>
              <dt>Admin access</dt>
              <dd>Cloudflare-managed</dd>
            </div>
          </dl>
        </section>

        <section className="panel-card">
          <header>
            <div>
              <span className="eyebrow">History</span>
              <h2>Recent activity</h2>
            </div>
            <a href="/admin/audit">Audit log</a>
          </header>
          <div className="activity-list">
            {auditEvents.slice(0, 6).map((event) => (
              <article key={event.id}>
                <span className="activity-dot" />
                <div>
                  <strong>{event.summary}</strong>
                  <small>
                    {event.actorEmail} · {formatDate(event.createdAt)}
                  </small>
                </div>
              </article>
            ))}
            {!auditEvents.length && (
              <p className="muted">
                No authentication or configuration events yet.
              </p>
            )}
          </div>
        </section>
      </div>
    </>
  );
}
